import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";

import { useForm } from "../../useForm";
import { useNavigate } from "react-router-dom";

import Input from "../../../controls/Input";
import CreatableSelectMenu from "../../../controls/CreatableSelectMenu";
import SelectMenu from "../../../controls/SelectMenu";
import TextArea from "../../../controls/TextArea";

import { format } from "date-fns";

import {
  classNames,
  conditionTypes,
  convertToDefEventPara,
  formatCurrency,
  ingestBarcodeScan,
  isEmployeeUserType,
  yesOrNoOptions,
} from "../../../../shared/utility";
import { makeGetApiCall } from "../../../../shared/makeApiCall";
import AssemblyItemsTableComponent from "../../../../viewmodels/receiving/invoices/AssemblyItemsTableComponent";
import DatePickerInput from "../../../controls/DatePickerInput";

import ConfirmPricePopup from "./ConfirmPricePopup";
import ReceivingBarcodeLabel from "./ReceivingBarcodeLabel";
import ExpandableDataTable from "../../../tables/ExpandableDataTable";
import { useRowDetailsState } from "../../../tables";
import EditProductSlideOver from "./EditProductSlideOver";

function DeleteMultipleItemsModal({
  selectedCount,
  onConfirm,
  setEmptyModelOpen,
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    const deleteSuccess = await onConfirm(reason);
    setSubmitting(false);

    if (deleteSuccess) {
      setEmptyModelOpen({ childComponent: null, open: false });
    }
  };

  return (
    <div>
      <div className="text-center">
        <h3 className="text-base font-medium text-gray-900">
          Delete selected items
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          You are about to delete {selectedCount} receiving item
          {selectedCount !== 1 ? "s" : ""}.
        </p>
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          className="inline-flex w-fit justify-center rounded-md bg-white px-3 py-2 text-sm font-light text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() =>
            setEmptyModelOpen({ childComponent: null, open: false })
          }
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex w-fit justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-light text-white hover:bg-red-500"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "Deleting..." : "Delete selected"}
        </button>
      </div>
    </div>
  );
}

// Three-level expandable table for invoice items
function InvoiceItemsTable({
  invoiceItems,
  invoiceId,
  isEmployee,
  openReceivingItemDetails,
  deleteReceivingItem,
  restoreReceivingItem,
  deleteMultipleReceivingItems,
  setInvoiceItems,
  setEmptyModelOpen,
  setSlideOver,
  showErrorModal,
}) {
  // State for lazy-loaded lots (Level 2)
  const {
    loadingRows: lotsLoading,
    setRowLoading: setLotLoading,
    rowDetails: lotsData,
    setRowDetails: setLotsData,
    updateRowDetails: patchLotsCache,
    clearRowDetails: clearLotsRowDetails,
  } = useRowDetailsState();

  // State for lazy-loaded items (Level 3)
  const {
    loadingRows: itemsLoading,
    setRowLoading: setItemLoading,
    rowDetails: itemsData,
    setRowDetails: setItemsData,
    updateRowDetails: patchItemsCache,
    clearRowDetails: clearItemsRowDetails,
  } = useRowDetailsState();

  // Controlled expanded rows so we can collapse all after product edit
  const [expandedRows, setExpandedRows] = useState({});
  const [checkedItemRows, setCheckedItemRows] = useState({});

  const mergeReceivingItemIntoItemsCache = useCallback(
    (receivingItem) => {
      if (receivingItem == null || receivingItem.id == null) return;
      const id = receivingItem.id;
      patchItemsCache((prev) => {
        let next = prev;
        let copied = false;
        Object.entries(prev).forEach(([cacheKey, list]) => {
          if (!Array.isArray(list)) return;
          let changed = false;
          const newList = list.map((row) => {
            if (row.receiving_item_id !== id) return row;
            changed = true;
            return {
              ...row,
              ...receivingItem,
              receiving_item_id: row.receiving_item_id,
            };
          });
          if (changed) {
            if (!copied) {
              next = { ...prev };
              copied = true;
            }
            next[cacheKey] = newList;
          }
        });
        return next;
      });
    },
    [patchItemsCache],
  );

  // Merge lots from items by lot used to update the lots data in the cache
  const mergeLotsFromItemsByLot = useCallback(
    (assemblyItemId, itemsByLot) => {
      if (
        assemblyItemId == null ||
        !Array.isArray(itemsByLot) ||
        itemsByLot.length === 0
      ) {
        return;
      }

      const cacheKey = String(assemblyItemId);

      patchLotsCache((prev) => {
        const lotMap = new Map();
        itemsByLot.forEach((lot) => {
          if (lot == null || lot.lot_number == null) return;
          lotMap.set(String(lot.lot_number), lot);
        });

        if (lotMap.size === 0) return prev;

        const existingLots = Array.isArray(prev[cacheKey])
          ? prev[cacheKey]
          : [];

        const nextLots = existingLots.map((row) => {
          const lotNumber = row?.lot_number;
          if (lotNumber == null) return row;
          const apiLot = lotMap.get(String(lotNumber));
          if (!apiLot) return row;
          lotMap.delete(String(lotNumber));
          return {
            ...row,
            ...apiLot,
          };
        });

        if (lotMap.size > 0) {
          lotMap.forEach((apiLot) => nextLots.push(apiLot));
        }

        return {
          ...prev,
          [cacheKey]: nextLots,
        };
      });
    },
    [patchLotsCache],
  );

  const handleDeleteReceivingItem = useCallback(
    async (receivingItemId) => {
      const callResult = await deleteReceivingItem(receivingItemId);
      if (callResult?.success && callResult.data?.receiving_item) {
        mergeReceivingItemIntoItemsCache(callResult.data.receiving_item);
        mergeLotsFromItemsByLot(
          callResult.data.receiving_item.assembly_item_id,
          callResult.data.items_by_lot_and_item,
        );
      }
      if (callResult?.success) {
        setCheckedItemRows((prev) => {
          const next = { ...prev };
          delete next[receivingItemId];
          return next;
        });
      }
    },
    [
      deleteReceivingItem,
      mergeLotsFromItemsByLot,
      mergeReceivingItemIntoItemsCache,
    ],
  );

  const handleRestoreReceivingItem = useCallback(
    async (receivingItemId) => {
      if (!restoreReceivingItem) return;
      const callResult = await restoreReceivingItem(receivingItemId);
      if (callResult?.success && callResult.data?.receiving_item != null) {
        mergeReceivingItemIntoItemsCache(callResult.data.receiving_item);
        mergeLotsFromItemsByLot(
          callResult.data.receiving_item.assembly_item_id,
          callResult.data.items_by_lot_and_item,
        );
      }
      if (callResult?.success) {
        setCheckedItemRows((prev) => {
          const next = { ...prev };
          delete next[receivingItemId];
          return next;
        });
      }
    },
    [
      restoreReceivingItem,
      mergeLotsFromItemsByLot,
      mergeReceivingItemIntoItemsCache,
    ],
  );

  const selectedReceivingItemIds = useMemo(
    () =>
      Object.keys(checkedItemRows)
        .filter((receivingItemId) => checkedItemRows[receivingItemId])
        .map((receivingItemId) => parseInt(receivingItemId, 10)),
    [checkedItemRows],
  );

  const onConfirmDeleteSelected = useCallback(
    async (reason) => {
      if (!invoiceId || selectedReceivingItemIds.length === 0) {
        showErrorModal("Please select at least one item.");
        return false;
      }

      if (!deleteMultipleReceivingItems) {
        showErrorModal("Bulk delete is not available.");
        return false;
      }

      const callResult = await deleteMultipleReceivingItems(
        invoiceId,
        selectedReceivingItemIds,
      );

      setCheckedItemRows({});
      setExpandedRows({});
      clearLotsRowDetails();
      clearItemsRowDetails();
      if (callResult?.success) {
        return true;
      }

      return false;
    },
    [
      invoiceId,
      selectedReceivingItemIds,
      deleteMultipleReceivingItems,
      showErrorModal,
      clearLotsRowDetails,
      clearItemsRowDetails,
    ],
  );

  const openDeleteSelectedModal = useCallback(() => {
    if (selectedReceivingItemIds.length === 0) {
      showErrorModal("Please select at least one item.");
      return;
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteMultipleItemsModal
          selectedCount={selectedReceivingItemIds.length}
          onConfirm={onConfirmDeleteSelected}
          setEmptyModelOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  }, [
    selectedReceivingItemIds,
    showErrorModal,
    setEmptyModelOpen,
    onConfirmDeleteSelected,
  ]);

  // After product save: collapse all rows and clear cached sub-items so they reload on next expand
  const onProductSaveSuccess = useCallback(() => {
    setExpandedRows({});
    setCheckedItemRows({});
    clearLotsRowDetails();
    clearItemsRowDetails();
  }, [clearLotsRowDetails, clearItemsRowDetails]);

  // Column definitions for Level 1 (Products)
  const productColumns = useMemo(() => {
    const columns = [
      { header: "Product Name", field: "product_name" },
      { header: "Total Cartons", field: "total_cartons", align: "right" },

      {
        header: "Total Unit",
        body: (row) =>
          `${row.received_amount} ${row.received_amount ? row.unit_of_measure : ""}`,
        align: "right",
      },
    ];

    if (!isEmployee) {
      columns.push({
        header: "Unit Cost",
        field: "unit_cost",
        body: (row) => formatCurrency(row.unit_cost),
        align: "right",
        width: "120px",
      });
      columns.push({
        header: "Total Cost",
        field: "total_cost",
        body: (row) => formatCurrency(row.total_cost),
        align: "right",
        width: "120px",
      });
      columns.push({
        header: "",
        body: (row) => (
          <button
            type="button"
            onClick={() =>
              setSlideOver({
                open: true,
                childComponent: (
                  <EditProductSlideOver
                    product={row}
                    invoiceId={invoiceId}
                    setSlideOver={setSlideOver}
                    setInvoiceItems={setInvoiceItems}
                    setEmptyModelOpen={setEmptyModelOpen}
                    showErrorModal={showErrorModal}
                    onSaveSuccess={onProductSaveSuccess}
                  />
                ),
              })
            }
            className="rounded-md bg-main-purple px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
          >
            Edit
          </button>
        ),
        width: "80px",
      });
    }

    return columns;
  }, [
    isEmployee,
    invoiceId,
    setSlideOver,
    setInvoiceItems,
    setEmptyModelOpen,
    showErrorModal,
    onProductSaveSuccess,
  ]);

  // Column definitions for Level 2 (Lots)
  const lotColumns = useMemo(() => {
    const columns = [
      { header: "Lot Number", field: "lot_number" },
      { header: "Total Cartons", field: "carton_count", align: "right" },
      {
        header: "Total Unit",
        body: (row) => `${row.received_amount} ${row.unit_of_measure}`,
        align: "right",
      },
    ];

    if (!isEmployee) {
      columns.push({
        header: "Total Cost",
        body: (row) => formatCurrency(row.total_cost),
        align: "right",
      });
    }

    return columns;
  }, [isEmployee]);

  const getItemColumns = useCallback(
    (items) => {
      const allItemsSelected =
        items.length > 0 &&
        items.every((row) => checkedItemRows[row.receiving_item_id]);
      const someItemsSelected =
        items.some((row) => checkedItemRows[row.receiving_item_id]) &&
        !allItemsSelected;

      const toggleSelectAll = () => {
        setCheckedItemRows((prev) => {
          const next = { ...prev };

          if (allItemsSelected) {
            items.forEach((row) => {
              delete next[row.receiving_item_id];
            });
            return next;
          }

          items.forEach((row) => {
            next[row.receiving_item_id] = true;
          });

          return next;
        });
      };

      const columns = [
        {
          header: (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={allItemsSelected}
                ref={(element) => {
                  if (element) {
                    element.indeterminate = someItemsSelected;
                  }
                }}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                aria-label="Select all items in this lot"
              />
            </div>
          ),
          width: "48px",
          body: (row) => (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={checkedItemRows[row.receiving_item_id]}
                onChange={() =>
                  setCheckedItemRows((prev) => ({
                    ...prev,
                    [row.receiving_item_id]: !prev[row.receiving_item_id],
                  }))
                }
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                aria-label={`Select item ${row.receiving_item_id}`}
              />
            </div>
          ),
        },
        {
          header: "Product Name",
          field: "product_name",
          headerStyle: { textAlign: "left" },
        },
        {
          header: "Barcode",
          field: "barcode",
          headerStyle: { textAlign: "left" },
        },
        {
          header: "Unit",
          body: (row) => `${row.received_amount} ${row.unit_of_measure}`,
          align: "right",
        },
      ];

      if (!isEmployee) {
        columns.push({
          header: "Unit Cost",
          body: (row) => formatCurrency(row.unit_cost),
          align: "right",
        });
        columns.push({
          header: "Total Cost",
          body: (row) => formatCurrency(row.total_cost),
          align: "right",
        });
      }

      columns.push({
        header: "",
        align: "right",
        body: (row) => (
          <div className="flex items-center justify-end gap-2 text-right">
            {row.deleted_at ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold uppercase text-black">
                  Deleted
                </span>

                {restoreReceivingItem ? (
                  <button
                    type="button"
                    onClick={() =>
                      handleRestoreReceivingItem(row.receiving_item_id)
                    }
                    className="rounded-md bg-green-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                  >
                    Restore
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    openReceivingItemDetails(row.receiving_item_id)
                  }
                  className="rounded-md bg-main-purple px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                >
                  View
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleDeleteReceivingItem(row.receiving_item_id)
                  }
                  className="rounded-md bg-red-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        ),
      });

      return columns;
    },
    [
      checkedItemRows,
      handleDeleteReceivingItem,
      handleRestoreReceivingItem,
      restoreReceivingItem,
      isEmployee,
      openReceivingItemDetails,
    ],
  );

  // Handle product row expand - fetch lots
  const handleProductExpand = useCallback(
    async (product) => {
      const key = product.assembly_item_id;
      if (lotsData[key]) return; // Already loaded

      setLotLoading(key, true);
      try {
        const callResult = await makeGetApiCall(
          `invoiceLots/${invoiceId}/${product.assembly_item_id}`,
        );
        if (callResult.success) {
          setLotsData(key, callResult.data?.items_by_lot_and_item ?? []);
        } else {
          console.error("Failed to fetch lots:", callResult.data?.message);
          setLotsData(key, []);
        }
      } catch (error) {
        console.error("Failed to fetch lots:", error);
        setLotsData(key, []);
      }
      setLotLoading(key, false);
    },
    [invoiceId, lotsData, setLotLoading, setLotsData],
  );

  // Handle lot row expand - fetch items
  const handleLotExpand = useCallback(
    async (lot, assemblyItemId) => {
      const key = `${assemblyItemId}_${lot.lot_number}`;
      if (itemsData[key]) return; // Already loaded

      setItemLoading(key, true);
      try {
        const callResult = await makeGetApiCall(
          `invoiceLotItems/${invoiceId}/${assemblyItemId}/${encodeURIComponent(lot.lot_number)}`,
        );
        if (callResult.success) {
          setItemsData(key, callResult.data.items || []);
        } else {
          console.error("Failed to fetch items:", callResult.data?.message);
          setItemsData(key, []);
        }
      } catch (error) {
        console.error("Failed to fetch items:", error);
        setItemsData(key, []);
      }
      setItemLoading(key, false);
    },
    [invoiceId, itemsData, setItemLoading, setItemsData],
  );

  // Render Level 3: Items table
  const renderItemsTable = useCallback(
    (lot, assemblyItemId) => {
      const key = `${assemblyItemId}_${lot.lot_number}`;
      const items = itemsData[key];

      if (itemsLoading[key]) {
        return (
          <div className="ml-3 p-4 text-center">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "1.5rem" }}
            />
            <p className="mt-2 text-gray-600">Loading items...</p>
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <div className="ml-3 p-4 text-center text-gray-500">
            No items available
          </div>
        );
      }

      const selectionRenderKey = items
        .map(
          (row) =>
            `${row.receiving_item_id}:${checkedItemRows[row.receiving_item_id] ? 1 : 0}`,
        )
        .join("|");

      return (
        <div className="ml-3">
          <ExpandableDataTable
            key={`${key}_${selectionRenderKey}`}
            data={items}
            dataKey="receiving_item_id"
            columns={getItemColumns(items)}
            tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
            cellClassName="py-2 bg-gray-50 border-b border-gray-200 px-2"
            wrapperClassName="receiving-invoice-items-table"
          />
        </div>
      );
    },
    [itemsLoading, itemsData, checkedItemRows, getItemColumns],
  );

  // Render Level 2: Lots table
  const renderLotsTable = useCallback(
    (product) => {
      const key = product.assembly_item_id;

      if (lotsLoading[key]) {
        return (
          <div className="ml-2 p-4 text-center">
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "1.5rem" }}
            />
            <p className="mt-2 text-gray-600">Loading lots...</p>
          </div>
        );
      }

      const lots = lotsData[key];
      if (!lots || lots.length === 0) {
        return (
          <div className="ml-2 p-4 text-center text-gray-500">
            No lots available
          </div>
        );
      }

      return (
        <div className="ml-2 p-1">
          <ExpandableDataTable
            data={lots}
            dataKey="unique_id"
            columns={lotColumns}
            expandable
            onRowExpand={(lot) =>
              handleLotExpand(lot, product.assembly_item_id)
            }
            renderExpandedRow={(lot) =>
              renderItemsTable(lot, product.assembly_item_id)
            }
            tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
            cellClassName="py-2 border-b border-gray-200 px-2"
            wrapperClassName="receiving-invoice-lots-table"
          />
        </div>
      );
    },
    [lotsLoading, lotsData, lotColumns, handleLotExpand, renderItemsTable],
  );

  const items = invoiceItems ?? [];
  const hasInvoiceItems = items.length > 0;

  // Calculate totals for summary footer (only shown when there are rows)
  const totalProducts = items.length;
  const totalCartons = items.reduce(
    (sum, p) => sum + parseFloat(p.total_cartons || 0),
    0,
  );
  const totalCost = items
    .reduce((sum, p) => sum + parseFloat(p.total_cost || 0), 0)
    .toFixed(2);

  const summaryFooter = hasInvoiceItems ? (
    <div className="p-4 bg-gray-100 border-t-2 border-gray-300">
      <div className="flex justify-between font-bold text-sm">
        <span>
          {totalProducts} product{totalProducts !== 1 ? "s" : ""}
        </span>
        <span>
          {totalCartons} carton{totalCartons !== 1 ? "s" : ""}
        </span>
        {!isEmployee && <span>${totalCost}</span>}
      </div>
    </div>
  ) : null;

  return (
    <div id="barcode-scanned-items-parent">
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={openDeleteSelectedModal}
          disabled={selectedReceivingItemIds.length === 0}
          className={classNames(
            selectedReceivingItemIds.length === 0
              ? "cursor-not-allowed bg-gray-300 text-gray-600"
              : "bg-red-600 text-white hover:bg-red-500",
            "rounded-md px-3 py-1 text-sm font-semibold shadow-sm",
          )}
        >
          Delete selected ({selectedReceivingItemIds.length})
        </button>
      </div>
      <div
        id="product-table"
        className="overflow-hidden card-custom overflow-x-auto"
      >
        <ExpandableDataTable
          data={items}
          dataKey="assembly_item_id"
          columns={productColumns}
          expandable
          expandedRows={expandedRows}
          onExpandedRowsChange={setExpandedRows}
          onRowExpand={handleProductExpand}
          renderExpandedRow={renderLotsTable}
          tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
          cellClassName="p-2 bg-white border-b border-gray-200 text-sm"
          wrapperClassName="receiving-invoice-products-table"
          emptyMessage="No items received yet"
          footer={summaryFooter}
        />
      </div>
    </div>
  );
}

const initialFValues = {
  invoice_number: "",
  date: "",
  assembly_item_id: "",
  is_barcode: { value: 2, label: "Barcode" },
  received_by: { value: -1, label: "" },
  manufacturer_id: "",
  supplier: { value: null, label: "" },
  product_name: "",
  lot_number: "",
  unit: "",
  received_amount: "",
  unit_cost: "",
  total_cost: "",
  pallet_condition: { value: 1, label: "Satisfactory" },
  product_condition: { value: 1, label: "Satisfactory" },
  product_quality: { value: 1, label: "Satisfactory" },
  cfia_meat: { value: 0, label: "N/A" },
  lab_results: { value: 0, label: "N/A" },
  is_halal: { value: 0, label: "N/A" },
  barcode: "",
  box_date: "",
  note: "",
  certification_type: 0,
  allergens: "",
  preservatives: "",
  product_type: 0,
  product_claims: 0,
  truck_temperature: "",
  truck_condition: { value: 1, label: "Satisfactory" },
  location: "Main",
  zone: "N/A",
  number_of_cartons: 1,
};

const barcodeOptions = [
  { value: 1, label: "No Barcode" },
  { value: 2, label: "Barcode" },
];

export default function AddReceivingInvoiceForm(props) {
  const {
    addItemToReceivingInvoice,
    barcodeDetails,
    createReceivingInvoice,
    getAllAssemblyItems,
    getAssemblyItemByPlu,
    invoice,
    invoiceItems,
    setInvoiceItems,
    onGenerateBarcodePdf,
    isEdit,
    setSlideOver,
    updateReceivingInvoiceApiCall,
    showErrorModal,
    openReceivingItemDetails,
    deleteReceivingItem,
    restoreReceivingItem,
    deleteMultipleReceivingItems,
    setEmptyModelOpen,
    employeeOptions,
    suppliers,
    printReceivingReportByID,
    getInvoiceProducts,
  } = props;

  const [barcodeScanInProgress, setBarcodeScanInProgress] = useState(false);
  const [createBarcodeInProgress, setCreateBarcodeInProgress] = useState(false);
  const [createInvoiceInProgress, setCreateInvoiceInProgress] = useState(false);
  const [updateInvoiceInProgress, setUpdateInvoiceInProgress] = useState(false);
  const [currentBarcodeItemPlu, setCurrentBarcodeItemPlu] = useState(null);
  const [lastAddedItemPayload, setLastAddedItemPayload] = useState(null);
  const [shouldShowBarcode, setShouldShowBarcode] = useState(false);

  const [isEmployee, setIsEmployee] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsEmployee(isEmployeeUserType());
  }, []);

  const validate = (fieldValues) => {
    let temp = { ...errors };
    temp.supplier = null;
    if (values.supplier.value < 1) {
      temp.supplier = "Please fill out field.";
    }

    // if ("is_barcode" in fieldValues) {
    //   temp.is_barcode = null;
    //   if (fieldValues.is_barcode.value < 1) {
    //     temp.is_barcode = "Please fill out field.";
    //   }
    // }

    if ("invoice_number" in fieldValues) {
      temp.invoice_number = null;
      if (fieldValues.invoice_number === "") {
        temp.invoice_number = "Please fill out field.";
      }
    }

    if ("received_by" in fieldValues || fieldValues.received_by === 0) {
      temp.received_by = null;
      if (fieldValues.received_by.value < 1) {
        temp.received_by = "Please fill out field.";
      }
    }

    if ("truck_temperature" in fieldValues) {
      temp.truck_temperature = null;
      if (fieldValues.truck_temperature === "") {
        temp.truck_temperature = "Please fill out field.";
      }
    }

    if ("truck_condition" in fieldValues) {
      temp.truck_condition = null;
      if (
        fieldValues.truck_condition === "" ||
        fieldValues.truck_condition === 0
      ) {
        temp.truck_condition = "Please fill out field.";
      }
    }

    // if ("received_amount" in fieldValues) {
    //   temp.received_amount = null;
    //   if (fieldValues.received_amount === "" && values.is_barcode.value === 1) {
    //     temp.received_amount = "Please fill out field.";
    //   }
    // }

    // if ("unit_cost" in fieldValues) {
    //   temp.unit_cost = null;
    //   if (fieldValues.unit_cost === "") {
    //     temp.unit_cost = "Please set a price.";
    //   }
    // }

    // if ("lot_number" in fieldValues) {
    //   temp.lot_number = null;
    //   if (fieldValues.lot_number === "") {
    //     temp.lot_number = "Please fill out field.";
    //     const ib = fieldValues.is_barcode ?? values.is_barcode;
    //     if (ib?.value === 2) {
    //       showErrorModal("Please choose a supplier item.");
    //     }
    //   }
    // }

    // if ("pallet_condition" in fieldValues) {
    //   temp.pallet_condition = null;
    //   if (
    //     fieldValues.pallet_condition === "" ||
    //     fieldValues.pallet_condition === 0
    //   ) {
    //     temp.pallet_condition = "Please fill out field.";
    //   }
    // }

    // if ("product_condition" in fieldValues) {
    //   temp.product_condition = null;
    //   const pc = fieldValues.product_condition;
    //   const pv =
    //     typeof pc === "object" && pc !== null && "value" in pc
    //       ? pc.value
    //       : pc;
    //   if (
    //     pv === "" ||
    //     pv === 0 ||
    //     pv === undefined ||
    //     pv === null
    //   ) {
    //     temp.product_condition = "Please fill out field.";
    //   }
    // }

    // if ("product_quality" in fieldValues) {
    //   temp.product_quality = null;
    //   const pq = fieldValues.product_quality;
    //   const qv =
    //     typeof pq === "object" && pq !== null && "value" in pq
    //       ? pq.value
    //       : pq;
    //   if (
    //     qv === "" ||
    //     qv === 0 ||
    //     qv === undefined ||
    //     qv === null
    //   ) {
    //     temp.product_quality = "Please fill out field.";
    //   }
    // }

    // if ("number_of_cartons" in fieldValues) {
    //   temp.number_of_cartons = null;
    //   const rawNc =
    //     fieldValues.number_of_cartons !== undefined
    //       ? fieldValues.number_of_cartons
    //       : values.number_of_cartons;
    //   const n = Number(rawNc);
    //   const barcodeMode =
    //     (fieldValues.is_barcode ?? values.is_barcode)?.value === 2;
    //   if (!Number.isFinite(n) || n < 1) {
    //     temp.number_of_cartons = "Enter at least 1 carton.";
    //   } else if (barcodeMode && n > 1) {
    //     temp.number_of_cartons = "Barcode mode allows at most 1 carton.";
    //   } else if (!barcodeMode && n > 500) {
    //     temp.number_of_cartons =
    //       "At most 500 cartons when generating barcodes.";
    //   }
    // }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === null);
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  const barcodeInputRef = useRef(null);

  // Normalize invoice data from API format to form format
  const normalizeInvoiceData = (invoiceData) => {
    if (!invoiceData) return invoiceData;

    const normalized = { ...invoiceData };

    // Helper function to normalize condition fields
    const normalizeCondition = (
      value,
      defaultValue = { value: 1, label: "Satisfactory" },
    ) => {
      if (typeof value === "object" && value !== null && "value" in value) {
        return value;
      } else if (typeof value === "number" || typeof value === "string") {
        const conditionValue =
          typeof value === "string" ? parseInt(value) : value;
        const foundCondition = conditionTypes.find(
          (c) => c.value === conditionValue,
        );
        return foundCondition || defaultValue;
      }
      return defaultValue;
    };

    // Normalize condition fields
    normalized.pallet_condition = normalizeCondition(
      invoiceData.pallet_condition,
    );
    normalized.truck_condition = normalizeCondition(
      invoiceData.truck_condition,
    );
    normalized.product_condition = normalizeCondition(
      invoiceData.product_condition,
    );
    normalized.product_quality = normalizeCondition(
      invoiceData.product_quality,
    );

    // Ensure is_barcode is in object format
    if (
      invoiceData.is_barcode === undefined ||
      typeof invoiceData.is_barcode !== "object" ||
      invoiceData.is_barcode === null ||
      !("value" in invoiceData.is_barcode)
    ) {
      normalized.is_barcode = { value: 2, label: "Barcode" };
    }

    return normalized;
  };

  useEffect(() => {
    if (isEdit && invoice) {
      const normalizedInvoice = normalizeInvoiceData(invoice);
      const firstItem = invoiceItems?.[0]?.sub_items?.[0];

      setValues({
        ...normalizedInvoice,
        invoice_id: invoice.id,
        is_barcode: { value: 2, label: "Barcode" },
        truck_temperature:
          firstItem?.truck_temperature ??
          normalizedInvoice.truck_temperature ??
          "",
      });
    } else {
      setValues({ ...values, date: format(new Date(), "LLL dd, YYY") });
    }
  }, [isEdit, invoice, invoiceItems, suppliers, employeeOptions]);

  useEffect(() => {
    setShouldShowBarcode(
      values.is_barcode?.value === 2 &&
        values.invoice_number !== "" &&
        typeof values.supplier === "object" &&
        values.supplier?.value > 0 &&
        values.received_by?.value > 0 &&
        values.zone !== "" &&
        values.location !== "" &&
        values.pallet_condition?.value > 0 &&
        values.truck_condition?.value > 0 &&
        values.truck_temperature !== "",
    );
  }, [values]);

  const openSearchAssemblyItems = async function () {
    var supplierId = values.supplier?.value;

    let callResult = await getAllAssemblyItems(supplierId);
    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <AssemblyItemsTableComponent
            assemblyItemsList={callResult.data.assembly_items}
            setItemValues={(assemblyItem) => {
              // if (assemblyItem.supplier_id == values.supplier.value) {
              setValues({
                ...values,
                barcode: "",
                assembly_item_id: assemblyItem.id,
                manufacturer_id: assemblyItem.manufacturer_id,
                manufacturer_name: assemblyItem.manufacturer_name,
                received_amount: assemblyItem.received_amount,
                product_name: assemblyItem.product_name,
                plu: assemblyItem.manufacturer_plu,
                unit_cost: assemblyItem.cost,
                lot_number: "",
                certification_type: assemblyItem.certification_type,
                product_type: assemblyItem.product_type,
                total_cost: values.received_amount * assemblyItem.cost,
                unit_of_measure: assemblyItem.unit_of_measure_text,
                ingredient_id: assemblyItem.ingredient_id,
              });
              // } else {
              //   showErrorModal("Suppliers do not match");
              // }
            }}
            setOpen={setSlideOver}
          />
        ),
        open: true,
      });
    } else {
    }
  };

  const continueAddingItemToInvoice = (newValues) => {
    if (invoice.id > 0) {
      addItemToInvoice(newValues);
    } else {
      onCreateInvoice(newValues);
    }
    setLastAddedItemPayload(newValues);
  };

  const handleBarcodeScan = async function (scannedBarcode) {
    if (scannedBarcode || scannedBarcode !== "") {
      setBarcodeScanInProgress(true);

      const barcodeInfo = ingestBarcodeScan(scannedBarcode);

      const barcodePlu = barcodeInfo.plu;
      if (currentBarcodeItemPlu !== barcodePlu) {
        setCurrentBarcodeItemPlu(barcodePlu);
      }

      if (barcodePlu || barcodePlu !== "") {
        let callResult = await getAssemblyItemByPlu(barcodePlu);

        if (callResult) {
          const newValues = {
            ...values,
            plu: barcodePlu,
            barcode: scannedBarcode,
            box_date: barcodeInfo.date,
            received_amount: barcodeInfo.weight,
            certification_type: callResult.certification_type["value"],
            manufacturer_id: callResult.manufacturer["value"],
            manufacturer_name: callResult.manufacturer["label"],
            unit_cost: callResult.cost,
            lot_number: values.lot_number,
            product_name: callResult.product_name,
            assembly_item_id: callResult.id,
            product_type: callResult.product_type["value"],
            total_cost: callResult.cost * barcodeInfo.weight,
            unit_of_measure: callResult.unit_of_measure,
            ingredient_id: callResult.ingredient_id,
          };

          // We show the cost popup then don't show it again unless plu changes
          if (currentBarcodeItemPlu === barcodePlu) {
            setEmptyModelOpen({
              childComponent: (
                <ConfirmPricePopup
                  assemblyItem={callResult}
                  newValues={newValues}
                  setNewCostForItem={continueAddingItemToInvoice}
                  setOpen={setEmptyModelOpen}
                  previousItem={lastAddedItemPayload}
                />
              ),
              open: true,
            });
          } else {
            setValues(newValues);
            setEmptyModelOpen({
              childComponent: (
                <ConfirmPricePopup
                  assemblyItem={callResult}
                  newValues={newValues}
                  setNewCostForItem={continueAddingItemToInvoice}
                  setOpen={setEmptyModelOpen}
                  previousItem={lastAddedItemPayload}
                />
              ),
              open: true,
            });
          }
        }
      }
      setBarcodeScanInProgress(false);
    }
  };

  const checkAssemblyItemChosen = (newValues) => {
    if (newValues.assembly_item_id < 1) {
      showErrorModal("Please choose a supplier item.");
      return false;
    } else {
      return true;
    }
  };

  const clearAssemblyItemValues = (newValues) => {
    const nextIsBarcode = newValues.is_barcode ?? values.is_barcode;
    const isBarcodeMode = nextIsBarcode?.value === 2;
    setValues({
      ...values,
      ...newValues,
      ...(newValues.is_barcode !== undefined
        ? { number_of_cartons: isBarcodeMode ? 1 : values.number_of_cartons }
        : {}),
      receiving_item_id: "",
      allergens: "",
      barcode: "",
      barcode_url: "",
      box_date: "",
      certification_type: "",
      manufacturer_id: "",
      manufacturer_name: "",
      received_amount: "",
      preservatives: "",
      product_claims: "",
      unit_cost: "",
      assembly_item_id: "",
      lot_number: "",
      plu: "",
      product_name: "",
      product_type: "",
      total_cost: "",
    });
  };

  const addItemToInvoice = async function (newValues) {
    var newValuesCopy = { ...newValues };
    if (!(Number(newValues.invoice_id) > 0)) {
      newValuesCopy = { ...newValuesCopy, invoice_id: invoice.id };
    }

    const validateResult = validate(newValuesCopy);
    if (validateResult && checkAssemblyItemChosen(newValuesCopy)) {
      var callResult = await addItemToReceivingInvoice(newValuesCopy);

      if (callResult.success) {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      } else {
        setValues({ ...values, scanned_box_label: "" });
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }
    } else {
      if (!validateResult) {
        showErrorModal("Please make sure you filled out all fields.");
      } else if (values.assembly_item_id < 1) {
        console.log("oggy error -2");
        showErrorModal("Please select a supplier item.");
      } else {
        showErrorModal("Please make sure you filled out all fields.");
      }
    }
  };

  const onCreateInvoice = async function (newValues) {
    if (validate(newValues)) {
      setCreateInvoiceInProgress(true);
      var callResult = await createReceivingInvoice(newValues);

      if (callResult.success) {
        const newInvoiceId = callResult.data.receiving_invoice.id;
        // setValues({
        //   ...values,
        //   invoice_id: newInvoiceId,
        // });
        // addItemToInvoice({
        //   ...newValues,
        //   invoice_id: newInvoiceId,
        // });

        navigate(`/receiving-invoices/${newInvoiceId}`);
      }

      setCreateInvoiceInProgress(false);
    } else {
      showErrorModal("Please make sure you filled out all fields.");
    }
  };

  const updateReceivingInvoice = async function () {
    setUpdateInvoiceInProgress(true);
    var callResult = await updateReceivingInvoiceApiCall(values, invoice.id);

    setUpdateInvoiceInProgress(false);
  };

  const generateBarcodeStringClicked = async function () {
    setCreateBarcodeInProgress(true);
    const callResult = await onGenerateBarcodePdf({
      receiving_item_id: values.receiving_item_id,
    });
    setCreateBarcodeInProgress(false);

    if (callResult.success) {
      setTimeout(() => {
        window.print();
      }, 1500);
    }
  };

  const handleTotalUnitKeyDown = (event) => {
    if (event.key === "Enter") {
      handleInputChange(
        convertToDefEventPara("received_amount", eval(values.received_amount)),
      );
    }
  };

  return (
    <div className="justify-between">
      <div className="hide-for-print">
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {isEdit
                ? "Editing invoice: " + invoice.invoice_number
                : "Add new receiving invoice"}
            </h3>
          </div>
        </div>

        <div className="pt-6 pb-5 space-y-5">
          {/* Invoice Info Section - Full Width */}
          <div className="p-4 card-custom">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <SelectMenu
                  handleInputChange={(e) => {
                    clearAssemblyItemValues({
                      is_barcode: e.target.value,
                      lot_number: values.lot_number,
                    });
                  }}
                  name="is_barcode"
                  value={values.is_barcode}
                  options={barcodeOptions}
                  title={"Is barcode"}
                  disabled={invoice.id < 1}
                  error={errors.is_barcode}
                />
              </div>
              <div>
                <DatePickerInput
                  label="Date"
                  name="date"
                  onChange={handleInputChange}
                  value={values.date}
                  labelOn={true}
                  type="text"
                  dateFormat={"LLL dd, YYY"}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div>
                <Input
                  label="Invoice #"
                  name="invoice_number"
                  onChange={handleInputChange}
                  value={values.invoice_number}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.invoice_number}
                />
              </div>
              <div>
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="supplier"
                  options={suppliers}
                  value={values.supplier}
                  title={"Supplier"}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <CreatableSelectMenu
                  handleInputChange={handleInputChange}
                  name="received_by"
                  options={employeeOptions}
                  defaultValue={isEdit ? invoice.received_by : null}
                  title={"Received by"}
                />
              </div>
              <div>
                <Input
                  label="Location"
                  name="location"
                  onChange={handleInputChange}
                  value={values.location}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.location}
                />
              </div>
              <div>
                <Input
                  label="Zone"
                  name="zone"
                  onChange={handleInputChange}
                  value={values.zone}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.zone}
                />
              </div>
              <div>
                <Input
                  label="Truck Temperature"
                  name="truck_temperature"
                  onChange={handleInputChange}
                  value={values.truck_temperature}
                  labelOn={true}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.truck_temperature}
                />
              </div>
              <div>
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="truck_condition"
                  options={conditionTypes}
                  value={values.truck_condition}
                  title="Truck Condition"
                  error={errors.truck_condition}
                />
              </div>
              <div>
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="pallet_condition"
                  value={values.pallet_condition}
                  options={conditionTypes}
                  title="Pallet Condition"
                  error={errors.pallet_condition}
                />
              </div>
            </div>
          </div>

          {/* Product Info Section - only after invoice exists; hidden when Barcode mode */}
          {invoice.id > 0 ? (
            <div
              className={classNames(
                values.is_barcode.value === 2 ? "hidden" : "p-4 card-custom",
              )}
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"></label>
                  <button
                    className="inline-flex justify-center py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-500 hover:bg-green-600 items-center w-full"
                    onClick={() => {
                      openSearchAssemblyItems();
                    }}
                  >
                    Search supplier items
                  </button>
                </div>
                <div>
                  <Input
                    label="Product name"
                    name="product_name"
                    onChange={handleInputChange}
                    value={values.product_name}
                    labelOn={true}
                    type="text"
                    disabled={true}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  />
                </div>
                <div>
                  <Input
                    label="Lot Number"
                    name="lot_number"
                    onChange={handleInputChange}
                    value={values.lot_number}
                    labelOn={true}
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    error={errors.lot_number}
                  />
                </div>
                <div>
                  <Input
                    label="Received amount"
                    name="received_amount"
                    onChange={handleInputChange}
                    value={values.received_amount}
                    labelOn={true}
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    error={errors.received_amount}
                  />
                </div>

                <div>
                  <Input
                    label="Number of cartons"
                    name="number_of_cartons"
                    onChange={handleInputChange}
                    value={values.number_of_cartons}
                    labelOn={true}
                    type="number"
                    min={1}
                    max={values.is_barcode.value === 2 ? 1 : 500}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    error={errors.number_of_cartons}
                  />
                </div>
                <div>
                  <Input
                    label="Unit of measure"
                    name="unit_of_measure"
                    onChange={handleInputChange}
                    value={values.unit_of_measure}
                    labelOn={true}
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    error={errors.unit_of_measure}
                    disabled={true}
                  />
                </div>
                <div>
                  <Input
                    label="Unit cost"
                    name="unit_cost"
                    onChange={handleInputChange}
                    value={values.unit_cost}
                    labelOn={true}
                    type="number"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    error={errors.unit_cost}
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="product_condition"
                    options={conditionTypes}
                    value={values.product_condition}
                    title="Product Condition"
                    error={errors.product_condition}
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="product_quality"
                    options={conditionTypes}
                    value={values.product_quality}
                    title="Product Quality"
                    error={errors.product_quality}
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="cfia_meat"
                    options={yesOrNoOptions}
                    value={values.cfia_meat}
                    title="CFIA Meat"
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="lab_results"
                    options={yesOrNoOptions}
                    value={values.lab_results}
                    title="Lab Results"
                  />
                </div>
                <div>
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="is_halal"
                    options={yesOrNoOptions}
                    value={values.is_halal}
                    title="Is Halal"
                  />
                </div>
                {values.is_barcode.value === 1 &&
                Number(values.number_of_cartons) > 1 ? (
                  <p className="col-span-2 sm:col-span-4 text-xs text-gray-600">
                    Total received amount is split evenly across cartons when
                    generating barcodes.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Barcode Input and Buttons */}
          <div className="flex flex-wrap items-center gap-4">
            {shouldShowBarcode && invoice.id > 0 ? (
              <div className="flex-1 min-w-64 flex items-center">
                <input
                  placeholder="Scan box label"
                  name="scanned_box_label"
                  disabled={barcodeScanInProgress}
                  ref={barcodeInputRef}
                  onChange={handleInputChange}
                  value={values.scanned_box_label}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleBarcodeScan(e.target.value);
                  }}
                  type="text"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <div className="my-auto flex items-center gap-4">
                <button
                  onClick={() => updateReceivingInvoice()}
                  className={classNames(
                    isEdit ? null : "hidden",
                    "inline-flex justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 items-center",
                  )}
                >
                  {createInvoiceInProgress ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "Update receiving"
                  )}
                </button>
                <button
                  onClick={() => onCreateInvoice(values)}
                  className={classNames(
                    invoice.id > 0 ? "hidden" : null,
                    "inline-flex justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center",
                  )}
                >
                  {createInvoiceInProgress ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "Create receiving"
                  )}
                </button>
                <button
                  onClick={() => addItemToInvoice(values)}
                  className={classNames(
                    invoice.id > 0 ? null : "hidden",
                    "inline-flex justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center",
                  )}
                >
                  {createInvoiceInProgress ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "Add item"
                  )}
                </button>
                <button
                  onClick={() => generateBarcodeStringClicked(values)}
                  className={classNames(
                    invoice.id > 0 &&
                      values.is_barcode.value === 1 &&
                      values.receiving_item_id > 0
                      ? "inline-flex justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 items-center"
                      : "hidden",
                  )}
                >
                  {createBarcodeInProgress ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "Print barcode"
                  )}
                </button>

                <button
                  onClick={() => printReceivingReportByID()}
                  className="inline-flex justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 items-center"
                >
                  Print Invoice
                </button>
              </div>
            </div>
          </div>

          {/* Invoice Items Table - Three Level Expandable */}
          <InvoiceItemsTable
            invoiceItems={invoiceItems}
            invoiceId={invoice?.id}
            isEmployee={isEmployee}
            openReceivingItemDetails={openReceivingItemDetails}
            deleteReceivingItem={deleteReceivingItem}
            restoreReceivingItem={restoreReceivingItem}
            deleteMultipleReceivingItems={deleteMultipleReceivingItems}
            setInvoiceItems={setInvoiceItems}
            setEmptyModelOpen={setEmptyModelOpen}
            setSlideOver={setSlideOver}
            showErrorModal={showErrorModal}
          />
        </div>
      </div>
      <ReceivingBarcodeLabel barcodeDetails={barcodeDetails} />
    </div>
  );
}
